"use client";

import { Compass, Droplet, Mountain, Sparkles } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function TrustArchitectures() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const pillars = [{
    index: "01 / IV",
    icon: Droplet,
    title: "Trust",
    titleItalic: "Architecture",
    body: "Customized legal frameworks designed to secure your assets, ensuring long-term legacy preservation through meticulous Maryland-based analysis.",
    coordTL: "39°00′N",
    coordBR: "76°45′W"
  }, {
    index: "02 / IV",
    icon: Mountain,
    title: "Estate",
    titleItalic: "Planning",
    body: "Strategic guidance for families seeking to navigate complex jurisdictional questions and protect their financial future with absolute clarity.",
    coordTL: "39°17′N",
    coordBR: "76°36′W"
  }, {
    index: "03 / IV",
    icon: Sparkles,
    title: "Sovereign",
    titleItalic: "Counsel",
    body: "Informed legal advice on sovereign citizenship, helping clients assert their rights responsibly while maintaining full regulatory compliance.",
    coordTL: "38°58′N",
    coordBR: "76°29′W"
  }, {
    index: "04 / IV",
    icon: Compass,
    title: "Legacy",
    titleItalic: "Governance",
    body: "A comprehensive approach to asset protection, mapping out your financial landscape to ensure your vision remains secure for generations.",
    coordTL: "39°05′N",
    coordBR: "76°50′W"
  }];
  return <section data-section-id="1450" ref={sectionRef} id="trust-architectures" className="dark bg-background text-foreground relative py-28 md:py-40 px-6 md:px-10 lg:px-16 border-t border-border font-default overflow-hidden">
      {}
      <div className="absolute top-0 left-0 right-0 h-px bg-border" aria-hidden="true" />

      {}
      <div className="absolute top-3 left-6 md:left-10 lg:left-16 right-6 md:right-10 lg:right-16 flex items-center justify-between text-[0.6875rem] font-mono uppercase tracking-[0.22em] text-muted-foreground">
        <span>§ VESTA // MARYLAND JURISDICTIONAL STATUS</span>
        <span className="hidden md:inline">DISPATCH 02</span>
      </div>

      <div className="mx-auto max-w-[1320px] grid grid-cols-1 lg:grid-cols-12 gap-10 md:gap-16">
        {}
        <div className="lg:col-span-4 lg:order-2">
          <div className="lg:sticky lg:top-24">
            <div className={`transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
              {}
              <div className="flex items-center gap-3 mb-8">
                <span className="text-primary font-mono text-base">§</span>
                <span className="font-mono text-[0.75rem] md:text-[0.8125rem] uppercase tracking-[0.22em] text-muted-foreground">
                  NO. 02 — DISPATCH
                </span>
              </div>

              {}
              <h2 className="font-serif font-normal text-foreground leading-[1.02] tracking-[-0.018em] mb-8" style={{
              fontSize: "clamp(2.25rem, 4.4vw, 4.25rem)"
            }}>
                The{" "}
                <span className="relative italic">
                  Architecture
                  <span className={`absolute left-0 right-0 -bottom-1.5 h-[2px] bg-primary origin-left transition-transform duration-[600ms] ease-out delay-[120ms] ${isVisible ? "scale-x-100" : "scale-x-0"}`} aria-hidden="true" />
                </span>{" "}
                of Autonomy
              </h2>

              {}
              <div className="flex items-center gap-2 mb-8" aria-hidden="true">
                <span className="h-1.5 w-1.5 rounded-lg bg-primary" />
                <span className="flex-1 border-t border-dashed border-border" />
                <span className="h-1.5 w-1.5 rounded-lg bg-primary" />
              </div>

              <p className="font-default text-foreground/85 leading-[1.65]" style={{
              fontSize: "clamp(0.9375rem, 1vw, 1rem)",
              letterSpacing: "0.008em"
            }}>
                VESTA is charted as a discipline of legal precision — four pillars of trust and sovereign counsel surveyed across the Maryland landscape. Each strategy is documented, measured, and refined through rigorous legal analysis, drawing from established trust law and the quiet science of asset protection. Consider this your dossier for legacy governance.
              </p>

              {}
              <div className="mt-12 relative aspect-[4/5] hidden lg:block">
                <div className="absolute inset-0 opacity-[0.18]">
                  <svg viewBox="0 0 240 300" className="w-full h-full" fill="none" stroke="currentColor" strokeWidth="0.8" aria-hidden="true">
                    {}
                    <path d="M40,90 Q35,75 50,65 Q70,55 90,60 Q110,50 130,55 Q150,45 175,55 Q200,65 205,85 Q210,105 195,120 Q200,140 185,155 Q190,175 170,185 Q150,200 125,195 Q100,205 80,195 Q55,200 45,180 Q30,165 35,145 Q25,125 40,110 Q35,100 40,90 Z" className="text-foreground" />
                    {}
                    <path d="M70,100 Q90,90 115,95 Q140,85 165,100 Q180,120 170,140 Q150,155 125,150 Q100,160 85,145 Q70,130 70,100 Z" className="text-foreground" strokeDasharray="2 3" />
                    <path d="M95,115 Q115,108 140,115 Q155,130 145,142 Q125,148 110,140 Q95,130 95,115 Z" className="text-foreground" strokeDasharray="2 3" />
                  </svg>
                </div>

                {}
                <svg viewBox="0 0 240 300" className="absolute inset-0 w-full h-full" fill="none" aria-hidden="true">
                  <path d="M120,160 L75,95 M120,160 L180,90 M120,160 L185,170 M120,160 L70,200" stroke="hsl(25 60% 55%)" strokeWidth="0.8" strokeDasharray="2 3" />
                  {}
                  <circle cx="120" cy="160" r="3" fill="hsl(25 60% 55%)" />
                  <circle cx="120" cy="160" r="6" stroke="hsl(25 60% 55%)" strokeWidth="0.6" fill="none" />
                  {}
                  <circle cx="75" cy="95" r="3" fill="hsl(25 60% 55%)" />
                  <circle cx="180" cy="90" r="3" fill="hsl(25 60% 55%)" />
                  <circle cx="185" cy="170" r="3" fill="hsl(25 60% 55%)" />
                  <circle cx="70" cy="200" r="3" fill="hsl(25 60% 55%)" />
                </svg>

                {}
                <div className="absolute top-2 left-2 font-mono text-[0.625rem] tracking-[0.14em] text-muted-foreground">
                  39°00′N
                </div>
                <div className="absolute bottom-2 right-2 font-mono text-[0.625rem] tracking-[0.14em] text-muted-foreground">
                  76°45′W
                </div>
                <div className="absolute bottom-2 left-2 font-mono text-[0.625rem] tracking-[0.14em] text-foreground/80">
                  ANNAPOLIS · MARYLAND
                </div>
              </div>
            </div>
          </div>
        </div>

        {}
        <div className="lg:col-span-8 lg:order-1">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-8">
            {pillars.map((pillar, i) => {
            const Icon = pillar.icon;
            return <article key={i} data-index={i} className={`group relative bg-card text-card-foreground border border-border rounded-none p-7 transition-all duration-300 ease-out hover:shadow-lg ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
              transitionDelay: isVisible ? `${i * 80}ms` : "0ms",
              transitionDuration: "720ms",
              transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)"
            }}>
                  {}
                  <div className="absolute top-2 left-2 flex items-center gap-1 pointer-events-none" aria-hidden="true">
                    <span className="block w-2 h-px bg-primary" />
                    <span className="block w-px h-2 bg-primary" />
                    <span className="font-mono text-[0.625rem] tracking-[0.14em] text-muted-foreground ml-1">
                      {pillar.coordTL}
                    </span>
                  </div>

                  {}
                  <div className="absolute bottom-2 right-2 flex items-center gap-1 pointer-events-none" aria-hidden="true">
                    <span className="font-mono text-[0.625rem] tracking-[0.14em] text-muted-foreground mr-1">
                      {pillar.coordBR}
                    </span>
                    <span className="block w-px h-2 bg-primary" />
                    <span className="block w-2 h-px bg-primary" />
                  </div>

                  {}
                  <div className="absolute top-4 right-4 w-12 h-12 bg-primary/10 border border-primary flex items-center justify-center opacity-0 scale-90 group-hover:opacity-100 group-hover:scale-100 transition-all duration-300 ease-out" aria-hidden="true">
                    <span className="font-serif italic text-primary text-lg leading-none">V</span>
                  </div>

                  {}
                  <div className="flex items-start justify-between mb-6 mt-3">
                    <div className="flex items-center justify-center w-11 h-11 border border-border text-foreground">
                      <Icon strokeWidth={1.25} className="w-5 h-5" />
                    </div>
                    <span className="font-mono text-[0.6875rem] uppercase tracking-[0.22em] text-muted-foreground pt-2">
                      {pillar.index}
                    </span>
                  </div>

                  {}
                  <h3 className="font-default font-medium text-foreground leading-[1.15] tracking-[-0.012em] mb-5" style={{
                fontSize: "clamp(1.5rem, 2.2vw, 2.125rem)"
              }}>
                    {pillar.title}{" "}
                    <span className="italic relative">
                      {pillar.titleItalic}
                      <span className="absolute left-0 right-0 -bottom-0.5 h-[2px] bg-primary" aria-hidden="true" />
                    </span>
                  </h3>

                  {}
                  <div className="flex items-center gap-2 mb-5" aria-hidden="true">
                    <span className="h-1.5 w-1.5 rounded-lg bg-primary" />
                    <span className="flex-1 border-t border-dashed border-border" />
                  </div>

                  {}
                  <p className="font-default text-foreground/80 leading-[1.65]" style={{
                fontSize: "0.9375rem",
                letterSpacing: "0.008em"
              }}>
                    {pillar.body}
                  </p>
                </article>;
          })}
          </div>
        </div>
      </div>
    </section>;
}